// English (default locale)
export default {
  nav: {
    brandSub: 'AWS Advanced Partner',
    links: {
      products: 'Services',
      solutions: 'Solutions',
      why: 'Why Us',
      about: 'About',
      news: 'News'
    },
    cta: 'Free Consultation →',
    ctaMobile: 'Free Consultation',
    menu: 'Menu',
    language: 'Language'
  },

  hero: {
    badge: 'AWS Advanced Partner · Trusted by 2,000+ companies',
    titlePre1: 'Cloud that ',
    titleHi1: 'drives innovation',
    titlePre2: 'Business that ',
    titleHi2: 'goes global',
    lead: 'As an AWS Advanced Partner, Easycloud delivers high-quality, cost-efficient one-stop AWS cloud solutions. From cloud migration and architecture optimization to managed operations, we help enterprises embrace digital transformation and expand into global markets.',
    ctaPrimary: 'Get a Free Consultation',
    ctaGhost: 'Explore Services ↓',
    stats: [
      { value: '2,000+', label: 'Enterprise clients' },
      { value: '5+', label: 'Global locations' },
      { value: '20+', label: 'AWS certified experts' }
    ],
    panelTitle: 'AWS Console',
    panelLive: 'Running',
    utilLabel: 'Global resource utilization',
    mini: [
      { k: 'Running instances', v: '3,200+', d: '+12%' },
      { k: 'Monthly savings', v: '38%', d: 'Cost optimized' },
      { k: 'Migration success', v: '99.8%', d: 'Zero downtime' },
      { k: 'Ops alerts', v: '0', d: 'Stable' }
    ],
    floatA: { title: 'AWS WAF Architecture', sub: 'Well-Architected' },
    floatB: { title: 'Global Edge', sub: '33 regions' },
    trust: ['Internet', 'Gaming', 'FinTech', 'E-commerce', 'SaaS', 'IoT', 'Social Media', 'EdTech']
  },

  products: {
    eyebrow: 'Our Services',
    titlePre: 'Five core services that ',
    titleHi: 'power your cloud journey',
    lead: 'As an AWS Advanced Partner, Easycloud provides full-lifecycle services — from cloud migration and architecture optimization to managed operations — helping enterprises cut costs, boost efficiency and accelerate innovation.',
    detailLink: 'Learn more →',
    items: [
      {
        title: 'Cloud Migration',
        desc: 'From assessment and discovery to migration delivery, we provide a complete methodology and framework for zero-disruption cloud adoption.',
        features: ['Workload assessment & planning', 'Automated migration & containerization', 'Migration validation & stress testing']
      },
      {
        title: 'Well-Architected Review',
        desc: 'Based on the AWS Well-Architected Framework, we comprehensively assess your architecture, surface risks and optimize the design.',
        features: ['Review across all six pillars', '1:1 architect guidance', 'Free architecture assessment report']
      },
      {
        title: 'DevOps Services',
        desc: 'AWS-based CI/CD and observability solutions that automate deployment, shorten release cycles and improve operational efficiency.',
        features: ['CI/CD pipeline setup', 'Prometheus / Grafana monitoring', 'DevOps culture & training']
      },
      {
        title: 'Managed Services (MSP)',
        desc: 'Fully managed 24/7 cloud operations with a dedicated client team, so you can focus on innovation while we run the cloud.',
        features: ['24/7 monitoring & alerting', 'Dedicated client working group', 'Cost optimization & billing analysis']
      },
      {
        title: 'Data & Analytics',
        desc: 'Built on AWS data lake and analytics services, we integrate multi-source data to enable business intelligence and data-driven decisions.',
        features: ['Data lake build & integration', 'BI dashboards & reporting', 'Machine learning & AI enablement']
      },
      {
        title: 'Account Management',
        desc: 'Full-lifecycle AWS account management covering cost optimization, product updates, technical support and training.',
        features: ['Usage reports & billing analysis', 'Budget alerts & cost control', 'New feature rollouts']
      }
    ]
  },

  solutions: {
    eyebrow: 'Industry Solutions',
    titlePre: 'Industry-focused ',
    titleHi: 'AWS cloud solutions',
    lead: 'Years of industry practice combined with the world-leading AWS infrastructure, delivering end-to-end scenario solutions that help enterprises reduce costs, boost efficiency and accelerate global expansion.',
    cta: 'Get the Industry Solution',
    items: [
      {
        name: 'Internet',
        title: 'Internet Cloud · Accelerate content delivery & global reach',
        desc: 'AWS architecture optimization for short-video and content platforms, dramatically reducing content load times and cross-region transfer costs.',
        points: ['CloudFront CDN global acceleration', 'Auto Scaling for traffic spikes', 'Low-cost storage & delivery with S3 + CloudFront'],
        metrics: [{ value: '70%', label: 'Lower load time' }, { value: '40%', label: 'Transfer cost saved' }]
      },
      {
        name: 'Gaming',
        title: 'Gaming Cloud · High-performance compute & global deployment',
        desc: 'Built on AWS high-performance compute, providing global server deployment, load balancing and low-latency experiences for game studios.',
        points: ['EC2 high-performance instances & GameLift', 'Multi-region active-active deployment', 'Low-latency access for global players'],
        metrics: [{ value: '80%', label: 'Fewer peak failures' }, { value: '<50ms', label: 'Global latency' }]
      },
      {
        name: 'FinTech',
        title: 'Finance Cloud · Data-driven precision decisions',
        desc: 'Leveraging AWS analytics services to provide customer profiling, real-time analytics and visual reporting for fintech companies.',
        points: ['Redshift / BigQuery analytics', 'Precise profiling & behavior prediction', 'Real-time dashboards & decision support'],
        metrics: [{ value: '3x', label: 'Analytics efficiency' }, { value: '99.99%', label: 'Service availability' }]
      },
      {
        name: 'IoT',
        title: 'IoT Cloud · Secure and reliable connected things',
        desc: 'Built on AWS IoT services, supporting millions of device connections while ensuring data security and device reliability.',
        points: ['Massive device onboarding with AWS IoT Core', 'Real-time data ingestion & analytics', 'Advanced security protocol protection'],
        metrics: [{ value: 'Millions', label: 'Device connections' }, { value: '99.9%', label: 'Data reliability' }]
      },
      {
        name: 'Retail',
        title: 'Retail Cloud · Elastic for every traffic surge',
        desc: 'Elastic architecture and intelligent operations for e-commerce, smoothly handling sales-peak surges and lifting conversion.',
        points: ['Auto Scaling for big sales events', 'Personalized recommendations & precision marketing', 'Omnichannel membership & transaction integration'],
        metrics: [{ value: '3x', label: 'Peak capacity' }, { value: '28%', label: 'Higher conversion' }]
      }
    ]
  },

  why: {
    eyebrow: 'Why Easycloud',
    titlePre: 'Why enterprises choose ',
    titleHi: 'Easycloud',
    lead: 'As an AWS Advanced Partner, we are built on technology and committed to service — delivering proven, professional technical consulting and cloud solutions to every client.',
    features: [
      { icon: '🏅', title: 'Certified Official Partner', desc: 'An AWS Advanced Consulting Partner with proven technical capability and rich service experience.' },
      { icon: '💡', title: 'Expert Guidance, Not Guesswork', desc: 'Proven professional consulting that makes your cloud decisions accurate, secure and aligned with long-term goals.' },
      { icon: '🔁', title: 'End-to-End Value', desc: 'From consulting and architecture to deployment, training and managed operations — full ownership that drives real business value.' },
      { icon: '💰', title: 'Save Time, Budget & Effort', desc: 'Avoid the cost of wrong technology choices and streamline execution, so you can focus on scaling your business.' },
      { icon: '🌏', title: 'Regional Leadership, Global Standards', desc: 'Deep roots across Asia-Pacific with a global outlook, delivering reliable cloud services for businesses going worldwide.' },
      { icon: '🤝', title: 'Localized Support', desc: 'Native-language L1/L2 support, faster incident response, with bank transfer and compliant invoicing.' }
    ],
    stats: [
      { value: '2,000+', label: 'Enterprise clients' },
      { value: '5+', label: 'Years of experience' },
      { value: '5+', label: 'Global locations' },
      { value: '20+', label: 'AWS certified experts' }
    ]
  },

  about: {
    eyebrow: 'About Easycloud',
    titleLine1: 'Empowering cloud innovation,',
    titleHi: 'making transformation simpler',
    text1: 'Founded in 2021, Easycloud is an AWS Advanced Partner. From five strategic hubs — Hong Kong, Shanghai, Tokyo, Singapore and Dubai — we deliver high-quality IT modernization solutions to enterprises worldwide.',
    text2: 'We believe cloud technology should simplify business, not add complexity. As an AWS Managed Service Provider (MSP), we design and operate intelligent, cost-efficient cloud environments across migration, architecture, DevOps, analytics and managed services for finance, retail, IoT, gaming, internet and social industries.',
    pillars: [
      { icon: '🎯', title: 'Mission', desc: 'Advance cloud solutions so enterprises run more efficiently and innovate faster' },
      { icon: '🌏', title: 'Vision', desc: 'To be the most trusted global AWS cloud partner for enterprises' },
      { icon: '💎', title: 'Values', desc: 'Technical excellence · Close collaboration · Results-driven' }
    ],
    certsLabel: 'Partner Certifications',
    certs: ['AWS Advanced Consulting Partner', 'AWS MSP Partner', 'Well-Architected Partner', 'AWS Certified Solutions Architect'],
    orbitSub: 'AWS Partner'
  },

  partners: {
    label: 'Trusted by leading companies across industries',
    quote: 'Easycloud helped us migrate our core systems to the cloud in three months. With an AWS Well-Architected Review optimizing our architecture, resource costs dropped by nearly 40%, and both stability and elasticity took a major leap. They are not just a cloud provider — they are a technology partner we trust.',
    authorName: 'Lin Min',
    authorRole: 'CTO · An Internet Technology Company',
    authorAvatar: 'L'
  },

  news: {
    eyebrow: 'News & Insights',
    titlePre: 'News and ',
    titleHi: 'industry insights',
    more: 'View all →',
    readMore: 'Read more →',
    featured: {
      tag: 'Event',
      title: 'AWS re:Invent 2025 wraps up: generative AI and agents take center stage',
      desc: 'AWS\'s flagship annual conference returned to Las Vegas, with keynotes spanning Amazon Bedrock, Amazon Q and agentic AI, alongside new compute, storage and database capabilities for building at scale.'
    },
    items: [
      { tag: 'Generative AI', title: 'Amazon Nova: a new generation of foundation models on Amazon Bedrock' },
      { tag: 'Database', title: 'Amazon Aurora DSQL: serverless distributed SQL with active-active high availability' },
      { tag: 'Developer Tools', title: 'Amazon Q Developer: a generative-AI assistant across the software lifecycle' },
      { tag: 'Compute', title: 'AWS Graviton4: more performance and better price-performance for Amazon EC2' }
    ]
  },

  contact: {
    eyebrow: 'Get Started',
    titlePre: 'Ready to begin your ',
    titleHi: 'AWS cloud journey',
    titleSuf: '?',
    lead: 'Leave your requirements and an Easycloud AWS architecture expert will contact you within one business day with a tailored cloud plan and quote.',
    info: {
      email: 'hello@easycloud.com',
      address: 'Hong Kong · Shanghai · Tokyo · Singapore · Dubai'
    },
    form: {
      name: 'Your name',
      namePlaceholder: 'Enter your name',
      contact: 'Contact',
      contactPlaceholder: 'Phone / email',
      company: 'Company',
      companyPlaceholder: 'Optional',
      message: 'Your needs',
      messagePlaceholder: 'Briefly describe your business scenario or requirements'
    },
    submit: 'Submit Inquiry',
    submitted: '✓ Submitted, thank you!',
    hint: 'By submitting you agree to Easycloud\'s privacy policy and terms of service'
  },

  footer: {
    desc: 'AWS Advanced Partner providing one-stop services for cloud migration, architecture optimization, DevOps and managed operations.',
    brandSub: 'AWS Partner',
    columns: [
      {
        title: 'Services',
        links: [
          { label: 'Cloud Migration', href: '#products' },
          { label: 'Well-Architected Review', href: '#products' },
          { label: 'DevOps Services', href: '#products' },
          { label: 'Managed Services (MSP)', href: '#products' }
        ]
      },
      {
        title: 'Solutions',
        links: [
          { label: 'Internet', href: '#solutions' },
          { label: 'Gaming', href: '#solutions' },
          { label: 'FinTech', href: '#solutions' },
          { label: 'IoT', href: '#solutions' }
        ]
      },
      {
        title: 'About',
        links: [
          { label: 'Company', href: '#about' },
          { label: 'News', href: '#news' },
          { label: 'Careers', href: '#contact' },
          { label: 'Contact', href: '#contact' }
        ]
      },
      {
        title: 'Support',
        links: [
          { label: 'Documentation', href: '#contact' },
          { label: 'Service Status', href: '#contact' },
          { label: 'Ticket System', href: '#contact' }
        ]
      }
    ],
    copyright: '© 2026 Easycloud. AWS Advanced Partner. All rights reserved.',
    legal: ['Privacy Policy', 'Terms of Service']
  },

  common: {
    backToTop: 'Back to top'
  }
}
